import * as React from 'react';
import type { SimpleTooltipProps } from '../simple-tooltip/simple-tooltip';
import { SimpleTooltip } from '../simple-tooltip/simple-tooltip';
import { Text, type TextProps } from '@components/atoms';
import { cn } from '@utils';

interface TextTooltipProps extends Omit<SimpleTooltipProps, 'children' | 'content'>, TextProps {
	children: string;
	disableWhenNotTruncated?: boolean;
	tooltipClassName?: string;
}

export const TextTooltip = ({
	children,
	tooltipClassName,
	contentClassName,
	disableWhenNotTruncated = true,
	...props
}: TextTooltipProps) => {
	const textRef = React.useRef<HTMLParagraphElement>(null);
	const [isTruncated, setIsTruncated] = React.useState(false);

	const updateOverflowState = () => {
		if (textRef.current) {
			setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
		}
	};

	React.useEffect(() => {
		window.addEventListener('resize', updateOverflowState);

		return () => {
			window.removeEventListener('resize', updateOverflowState);
		};
	}, []);

	React.useEffect(() => {
		updateOverflowState();
	}, [children]);

	return (
		<SimpleTooltip
			disabled={!isTruncated && disableWhenNotTruncated}
			content={children}
			ariaLabel={children}
			className={cn(tooltipClassName, 'flex min-w-0')}
			contentClassName={contentClassName}
		>
			<Text ref={textRef} {...props} truncate>
				{children}
			</Text>
		</SimpleTooltip>
	);
};
